import { Box, Button, Flex, Text, Link } from "@chakra-ui/react";
import { Avatar } from "../user/Avatar";
import { SealCheckThickIcon } from "../icons/SealCheckThickIcon";
import { BlogCommentEntity, User } from "../../types";
import { BorderedTag } from "../tags/BorderedTag";
import { t } from "i18next";
import { ContentEditable } from "../text/ContentEditable";
import { memo, useState } from "react";
import { CommentPopoverMenu } from "./CommentPopoverMenu";
import { CommentVoteButton } from "./CommentVoteButton";
import { useUserData } from "../../hooks/useUserData";
import { useCommentEditState } from "./hooks/useCommentEditState";
import { useCommentReplyState } from "./hooks/useCommentReplyState";
import { setFunnelModalConfig } from "../funnel/FunnelModal";
import { FUNNEL_TYPES } from "../funnel/utils/contants";
import { FunnelContextArea } from "../funnel/types";
import { styleUsernamesInComment } from "./utils/styleCommentText";
import { abbreviateTime } from "../../utils/abbreviateTime";
import { useSlideInMenu } from "../slideInMenu/hooks/useSlideInMenu";
import { useIsInReader } from "../../hooks/useIsInReader";

interface CommentProps {
  comment: BlogCommentEntity;
  editComment?: (comment: BlogCommentEntity, body: string) => void;
  deleteComment?: (comment: BlogCommentEntity) => void;
  reactToComment?: (comment: BlogCommentEntity, reactionTypeId: number) => void;
  submitReply?: (body: string, parentComment?: BlogCommentEntity) => void;
  clearReplyState?: () => void;
  onTriggerReply?: (targetComment: BlogCommentEntity) => void;
  // Used to track active state of reply button
  replyingToCommentId?: number;
  compact?: boolean;
  contentEditable?: boolean;
  startInEditMode?: boolean;
  // Context for login funnel tracking - differentiates news vs release comments
  funnelContextArea?: FunnelContextArea;
  commentsDisabled?: boolean;
}

export const Comment = memo(
  ({
    comment,
    reactToComment,
    editComment,
    deleteComment,
    submitReply,
    clearReplyState,
    onTriggerReply,
    replyingToCommentId,
    compact,
    contentEditable = false,
    startInEditMode = false,
    funnelContextArea,
    commentsDisabled = false,
  }: CommentProps) => {
    const { userData } = useUserData();
    const { openUserProfile } = useSlideInMenu();
    const isInReader = useIsInReader();

    const {
      editableRef,
      isEditing,
      startEditing,
      cancelEditing,
      submitEdit,
      handleSubmit,
    } = useCommentEditState({
      comment,
      startInEditMode,
      onEdit: editComment,
    });

    const {
      replyingToComment,
      isTopLevel,
      handleReplyToComment,
      clearReplyState: clearLocalReplyState,
    } = useCommentReplyState({
      comment,
      onTriggerReply,
    });

    // Only top-level comments can show reply forms
    const shouldShowReplyForm = isTopLevel && replyingToComment && submitReply;

    // Handle clear reply state - use parent's clearReplyState if available
    const handleClearReplyState = () => {
      if (clearReplyState) {
        clearReplyState();
      } else {
        clearLocalReplyState();
      }
    };

    const {
      author: { name, gold, is_admin: isAdmin, avatar_tiny_url },
      can_edit,
      can_moderate: canModerate,
      parsed_body,
      time_ago,
      level = 0,
    } = comment;

    const canEdit = can_edit && contentEditable;
    const timeAgo = abbreviateTime(time_ago);

    // Track if content has changed from initial value
    const [hasContentChanged, setHasContentChanged] = useState(false);
    // For editing, use plain text content without HTML styling
    const plainTextContent = (parsed_body || "").replace(/<[^>]*>/g, "");
    // For display, apply styling
    const displayContent = isEditing
      ? plainTextContent
      : styleUsernamesInComment(parsed_body || "");

    const handleContentChange = () => {
      if (editableRef.current) {
        const currentText = editableRef.current.innerText.trim();
        setHasContentChanged(currentText !== plainTextContent.trim());
      }
    };

    return (
      <Box
        w="full"
        display="flex"
        aria-label={`${name}'s message at ${timeAgo}`}
        height="fit-content"
        position="relative"
        gap="12px"
        p={level === 1 && !compact ? "8px" : level > 1 ? "16px" : "0px"}
        border="1px solid"
        borderColor={
          level === 1
            ? "transparent"
            : isEditing && replyingToCommentId
              ? "transparent.white.30"
              : "transparent.white.05"
        }
        bg={
          (isEditing && replyingToCommentId) || level === 1
            ? "transparent"
            : "transparent.white.05"
        }
        borderRadius="24px"
        justifyContent="flex-start"
        alignItems="flex-start"
        width="100%"
        minWidth={0}
        lineHeight="normal"
        letterSpacing="normal"
      >
        <Link>
          <Avatar
            size={compact ? "md" : "lg"}
            isGold={gold || false}
            username={name || t("components.comments.anonymous")}
            imageSrc={avatar_tiny_url || ""}
            openProfileOnClick
            contextArea={
              isInReader ? FunnelContextArea.reader : FunnelContextArea.news
            }
          />
        </Link>

        <Flex flexDirection="column" gap="12px" width="100%" minWidth={0}>
          <Flex flexDirection="column" flexGrow={1} minWidth={0}>
            {isEditing && replyingToCommentId && comment.id === -1 ? null : (
              <Flex
                alignItems="center"
                width="100%"
                gap="8px"
                maxWidth="calc(100%)"
                justifyContent="space-between"
              >
                <Flex gap="8px" alignItems="center" flex="1" minWidth={0}>
                  <Link
                    onClick={
                      name
                        ? () =>
                            openUserProfile(name, {
                              area: isInReader
                                ? FunnelContextArea.reader
                                : FunnelContextArea.news,
                            })
                        : undefined
                    }
                    textDecoration="none"
                    _hover={{ textDecoration: "underline" }}
                  >
                    <Text
                      fontWeight="600"
                      fontSize="16px"
                      color={isAdmin ? "blaze.blaze" : "dune.dune"}
                      title={name || "Anonymous"}
                      textOverflow="ellipsis"
                      overflow="hidden"
                      whiteSpace="nowrap"
                      flexShrink={0}
                      _hover={{ textDecoration: "underline" }}
                    >
                      {name || "Anonymous"}
                    </Text>
                  </Link>
                  {!compact && gold ? (
                    <SealCheckThickIcon
                      color="blaze.blaze"
                      boxSize="16px"
                      flexShrink={0}
                    />
                  ) : null}
                  <Text
                    fontWeight="500"
                    fontSize="12px"
                    color="dune.700"
                    textOverflow="ellipsis"
                    overflow="hidden"
                    whiteSpace="nowrap"
                    title={timeAgo || ""}
                    flexShrink={1}
                    minWidth={0}
                  >
                    {timeAgo || ""}
                  </Text>
                  {isAdmin && <BorderedTag text="Admin" />}
                </Flex>
                {contentEditable && (canModerate || canEdit) && (
                  <CommentPopoverMenu
                    onDelete={
                      canModerate ? () => deleteComment?.(comment) : undefined
                    }
                    onEdit={canEdit ? startEditing : undefined}
                  />
                )}
              </Flex>
            )}
            <Flex flexDirection="column" width="100%" minWidth={0}>
              <ContentEditable
                passedEditableRef={editableRef}
                enabled={canEdit && isEditing}
                fontSize="16px"
                textAlign="left"
                whiteSpace="normal"
                wordBreak="break-word"
                overflowWrap="anywhere"
                fontWeight="400"
                lineHeight="150%"
                color="dune.dune"
                dangerouslySetInnerHTML={{ __html: displayContent }}
                outline="none"
                handleSubmit={handleSubmit}
                onInput={handleContentChange}
              />
              {!isEditing && !commentsDisabled && (
                <Flex gap="8px" pt="6px" alignItems="center">
                  {reactToComment && (
                    <CommentVoteButton
                      toggleVote={(reactionTypeId) => {
                        if (userData) {
                          reactToComment(comment, reactionTypeId);
                        } else {
                          setFunnelModalConfig({
                            type: FUNNEL_TYPES.signup,
                            context: {
                              modalContext:
                                funnelContextArea || FunnelContextArea.comment,
                            },
                            options: {
                              onSuccess: () =>
                                reactToComment(comment, reactionTypeId),
                            },
                          });
                        }
                      }}
                      userVoteType={comment.user_vote_reaction_type_id}
                      upvoteCount={comment.total_good ?? 0}
                      downvoteCount={comment.total_bad ?? 0}
                    />
                  )}
                  {(submitReply || onTriggerReply) && (
                    <Button
                      cursor="pointer"
                      fontSize="12px"
                      fontWeight="500"
                      letterSpacing=".5px"
                      color="dune.700"
                      borderRadius="100px"
                      background="transparent.white.10"
                      p="8px 12px"
                      onClick={() => {
                        if (userData) {
                          handleReplyToComment(comment);
                        } else {
                          setFunnelModalConfig({
                            type: FUNNEL_TYPES.signup,
                            context: {
                              modalContext:
                                funnelContextArea || FunnelContextArea.comment,
                            },
                            options: {
                              onSuccess: () => handleReplyToComment(comment),
                            },
                          });
                        }
                      }}
                      border="1px solid"
                      borderColor="transparent.white.10"
                      _hover={{ color: "dune.100" }}
                      _active={{ color: "dune.100", borderColor: "dune.100" }}
                      isActive={
                        comment.id === replyingToCommentId ||
                        comment.id === replyingToComment?.id
                      }
                    >
                      {t("components.comments.reply")}
                    </Button>
                  )}
                </Flex>
              )}
            </Flex>
          </Flex>
          {comment.replies && comment.replies.length > 0 && (
            <>
              {comment.replies.map((reply) => (
                <Comment
                  key={reply.id}
                  comment={reply}
                  reactToComment={reactToComment}
                  editComment={editComment}
                  deleteComment={deleteComment}
                  submitReply={submitReply}
                  replyingToCommentId={
                    replyingToCommentId ?? replyingToComment?.id
                  }
                  onTriggerReply={
                    isTopLevel ? handleReplyToComment : onTriggerReply
                  }
                  compact={compact}
                  contentEditable={canEdit}
                  funnelContextArea={funnelContextArea}
                  commentsDisabled={commentsDisabled}
                />
              ))}
            </>
          )}
          {shouldShowReplyForm && userData && replyingToComment && (
            <Comment
              comment={{
                id: -1,
                body: "",
                level: comment.level + 1,
                author: {
                  id: userData.id,
                  name: userData.name || "Anonymous",
                  gold: userData.gold || false,
                  is_admin: userData.is_admin || false,
                  avatar_tiny_url: userData.avatar_tiny_url || "",
                } as User,
                entity_type: comment.entity_type,
                object_id: comment.object_id,
                parent_id: comment.parent_id,
                total_comments: 0,
                page_id: null,
                parsed_body: `<span style="color: var(--chakra-colors-dune-700)">@${replyingToComment.author.name}:</span>&nbsp;`,
                author_id: userData.id,
                post_date: "",
                edit_user_id: null,
                edit_date: null,
                is_deleted: 0,
                artist_id: 0,
                pos: null,
                comment_type: "",
                can_edit: true,
                can_moderate: true,
                time_ago: "",
                edit_time_ago: null,
                url: "",
                delete_url: "",
                edit_url: "",
                author_is_context_owner: true,
                replies: [],
                total_good: 0,
                total_bad: 0,
                user_vote_reaction_type_id: null,
              }}
              startInEditMode
              contentEditable
              replyingToCommentId={replyingToCommentId ?? replyingToComment.id}
              editComment={(_, body) => {
                // This is a reply form, so we submit a reply, not edit the parent
                submitReply?.(body, replyingToComment);
                handleClearReplyState();
              }}
              submitReply={(body) => {
                submitReply?.(body, replyingToComment);
              }}
              clearReplyState={handleClearReplyState}
              compact={compact}
              funnelContextArea={funnelContextArea}
            />
          )}
          {isEditing && (
            <Flex justifyContent="flex-end" gap="8px">
              <Button
                variant="tertiary"
                size="sm"
                onClick={() => {
                  if (replyingToCommentId && comment.id === -1) {
                    // This is a reply form (fake comment with id -1)
                    handleClearReplyState();
                  } else {
                    // This is editing an existing comment
                    cancelEditing();
                  }
                }}
              >
                Cancel
              </Button>
              <Button
                variant="primary"
                size="sm"
                isDisabled={!hasContentChanged}
                onClick={() => {
                  if (replyingToCommentId && comment.id === -1) {
                    // This is a reply form (fake comment with id -1)
                    if (editableRef.current) {
                      submitReply?.(editableRef.current.innerText);
                    }
                    handleClearReplyState();
                  } else {
                    // This is editing an existing comment
                    submitEdit();
                  }
                }}
              >
                Comment
              </Button>
            </Flex>
          )}
        </Flex>
      </Box>
    );
  },
);
